<template>
  <div>
    <!-- Main Add/Edit Internship Track Dialog -->
    <v-dialog v-model="dialog" eager persistent scrollable max-width="900">
      <v-form ref="DSSCriterionForm" @submit.prevent>
        <v-card rounded="xl">
          <!-- Header -->
          <div class="dlg-header d-flex align-center px-6 py-4">
            <div class="d-flex align-center ga-3">
              <div
                class="dlg-icon-box rounded-lg d-flex align-center justify-center"
              >
                <v-icon color="white" size="20">mdi-format-list-checks</v-icon>
              </div>
              <div>
                <div style="font-size: 17px; font-weight: 700; color: #111827">
                  {{ action }} Internship Track
                </div>
                <div style="font-size: 12.5px; color: #6b7280">
                  Define the track title, description, and matching criteria
                </div>
              </div>
            </div>
            <v-spacer />
            <v-btn
              icon="mdi-close"
              variant="text"
              density="comfortable"
              @click="closeD()"
            />
          </div>
          <v-divider />

          <v-card-text class="pa-6" style="max-height: 70vh; overflow-y: auto">
            <v-row>
              <!-- Basic Info -->
              <v-col cols="12">
                <div class="section-label mb-3">Basic Information</div>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="title"
                  :rules="[formRules.required]"
                  label="Track Title"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  prepend-inner-icon="mdi-text-box-outline"
                  placeholder="e.g. Software Development Track"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="description"
                  :rules="[formRules.required]"
                  label="Description"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  rows="3"
                  prepend-inner-icon="mdi-text"
                  placeholder="Describe what this internship track involves..."
                />
              </v-col>

              <!-- Criteria Section -->
              <v-col cols="12">
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="section-label">Matching Criteria</div>
                  <v-btn
                    color="#00A63E"
                    variant="tonal"
                    prepend-icon="mdi-plus"
                    rounded="lg"
                    size="small"
                    @click="add_criteria"
                    >Add Criterion</v-btn
                  >
                </div>
                <div class="mb-3" style="font-size: 12px; color: #9ca3af">
                  Total weight:
                  <span
                    :style="{
                      color:
                        getTotalCriteriaWeights == 100 ? '#059669' : '#d97706',
                      fontWeight: 700,
                    }"
                    >{{ getTotalCriteriaWeights }}%</span
                  >
                  <span style="color: #9ca3af"> / 100%</span>
                </div>

                <!-- Empty state -->
                <div
                  v-if="criteriaList.length == 0"
                  class="no-criteria-box rounded-xl d-flex flex-column align-center justify-center py-8"
                >
                  <v-icon size="40" color="#d1d5db"
                    >mdi-filter-cog-outline</v-icon
                  >
                  <div
                    class="mt-2"
                    style="font-size: 14px; font-weight: 600; color: #6b7280"
                  >
                    No criteria added yet
                  </div>
                  <div style="font-size: 12px; color: #9ca3af">
                    Click "Add Criterion" to start building matching criteria
                  </div>
                </div>

                <!-- Criteria cards -->
                <div v-else>
                  <div
                    v-for="(i, idx) in criteriaList"
                    :key="idx"
                    class="criteria-card rounded-xl pa-4 mb-3"
                  >
                    <div class="d-flex align-start justify-space-between">
                      <div
                        class="d-flex align-center ga-3"
                        style="flex: 1; min-width: 0"
                      >
                        <div
                          class="criteria-num rounded-lg d-flex align-center justify-center flex-shrink-0"
                        >
                          {{ idx + 1 }}
                        </div>
                        <div style="flex: 1; min-width: 0">
                          <div
                            style="
                              font-size: 14px;
                              font-weight: 700;
                              color: #111827;
                            "
                          >
                            {{ i.name }}
                          </div>
                          <div class="d-flex align-center ga-2 mt-1 flex-wrap">
                            <v-chip
                              size="x-small"
                              :color="
                                i.criteria_optimization == 'maximize'
                                  ? 'green'
                                  : 'orange'
                              "
                              variant="tonal"
                            >
                              <v-icon start size="10">
                                {{
                                  i.criteria_optimization == "maximize"
                                    ? "mdi-arrow-up"
                                    : "mdi-arrow-down"
                                }}
                              </v-icon>
                              {{
                                i.criteria_optimization == "maximize"
                                  ? "Maximize"
                                  : "Minimize"
                              }}
                            </v-chip>
                            <v-chip size="x-small" color="blue" variant="tonal">
                              <v-icon start size="10">mdi-weight</v-icon>
                              {{ i.weight }}%
                            </v-chip>
                          </div>
                        </div>
                      </div>
                      <div class="d-flex align-center ga-1 ml-2">
                        <v-btn
                          icon
                          size="small"
                          variant="text"
                          color="#6b7280"
                          @click="edit_criteria(i, idx)"
                        >
                          <v-icon size="16">mdi-pencil-outline</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          size="small"
                          variant="text"
                          color="error"
                          @click="delete_criteria(idx)"
                        >
                          <v-icon size="16">mdi-delete-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>
                    <div
                      v-if="i.skill_required && i.skill_required.length"
                      class="d-flex flex-wrap ga-1 mt-3"
                    >
                      <span
                        style="
                          font-size: 11.5px;
                          color: #9ca3af;
                          align-self: center;
                        "
                      >
                        Skills:
                      </span>
                      <v-chip
                        v-for="s in i.skill_required"
                        :key="s.id"
                        size="x-small"
                        color="green"
                        variant="tonal"
                        >{{ s.title }}</v-chip
                      >
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />
          <v-card-actions class="px-6 py-3">
            <v-spacer />
            <v-btn variant="text" color="#6b7280" rounded="lg" @click="closeD()"
              >Cancel</v-btn
            >
            <v-btn
              color="#00A63E"
              variant="flat"
              class="text-white"
              rounded="lg"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="action == 'Add' ? save() : update()"
            >
              <v-icon start>mdi-check-circle-outline</v-icon>
              {{ action == "Add" ? "Save Track" : "Update Track" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- Add/Edit Criterion Sub-dialog -->
    <v-dialog
      v-model="add_criteria_dialog"
      eager
      persistent
      scrollable
      max-width="620"
    >
      <v-form ref="CriterionForm" @submit.prevent>
        <v-card rounded="xl">
          <!-- Header -->
          <div class="dlg-header d-flex align-center px-6 py-4">
            <div class="d-flex align-center ga-3">
              <div
                class="dlg-icon-box rounded-lg d-flex align-center justify-center"
              >
                <v-icon color="white" size="20">mdi-filter-cog-outline</v-icon>
              </div>
              <div>
                <div style="font-size: 17px; font-weight: 700; color: #111827">
                  {{ criteriaAction }} Criterion
                </div>
                <div style="font-size: 12.5px; color: #6b7280">
                  Set weight, optimization direction, and required skills
                </div>
              </div>
            </div>
            <v-spacer />
            <v-btn
              icon="mdi-close"
              variant="text"
              density="comfortable"
              @click="add_criteria_dialog = false"
            />
          </div>
          <v-divider />

          <v-card-text class="pa-6" style="max-height: 65vh; overflow-y: auto">
            <v-row>
              <v-col cols="12" md="7">
                <v-text-field
                  v-model="criteria_name"
                  :rules="[formRules.required]"
                  label="Criterion Name"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  prepend-inner-icon="mdi-tag-outline"
                  placeholder="e.g. GPA, Technical Skills"
                />
              </v-col>
              <v-col cols="12" md="5">
                <v-text-field
                  type="number"
                  v-model="weight"
                  :rules="[formRules.required]"
                  label="Weight (%)"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  prepend-inner-icon="mdi-percent-outline"
                  placeholder="e.g. 40"
                />
              </v-col>

              <!-- Optimization Direction -->
              <v-col cols="12">
                <div class="section-label mb-2">Optimization Direction</div>
                <div class="d-flex ga-3">
                  <div
                    class="opt-card rounded-xl pa-4 d-flex align-center ga-3"
                    :class="
                      criteria_optimization == 'maximize'
                        ? 'opt-card--green'
                        : ''
                    "
                    style="flex: 1; cursor: pointer"
                    @click="criteria_optimization = 'maximize'"
                  >
                    <v-icon
                      :color="
                        criteria_optimization == 'maximize'
                          ? '#059669'
                          : '#9ca3af'
                      "
                      size="24"
                      >mdi-arrow-up-circle-outline</v-icon
                    >
                    <div>
                      <div
                        style="
                          font-size: 13px;
                          font-weight: 700;
                          color: #111827;
                        "
                      >
                        Maximize
                      </div>
                      <div style="font-size: 11.5px; color: #6b7280">
                        Higher is better
                      </div>
                    </div>
                  </div>
                  <div
                    class="opt-card rounded-xl pa-4 d-flex align-center ga-3"
                    :class="
                      criteria_optimization == 'minimize'
                        ? 'opt-card--orange'
                        : ''
                    "
                    style="flex: 1; cursor: pointer"
                    @click="criteria_optimization = 'minimize'"
                  >
                    <v-icon
                      :color="
                        criteria_optimization == 'minimize'
                          ? '#d97706'
                          : '#9ca3af'
                      "
                      size="24"
                      >mdi-arrow-down-circle-outline</v-icon
                    >
                    <div>
                      <div
                        style="
                          font-size: 13px;
                          font-weight: 700;
                          color: #111827;
                        "
                      >
                        Minimize
                      </div>
                      <div style="font-size: 11.5px; color: #6b7280">
                        Lower is better
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="optimizationError"
                  class="mt-1"
                  style="font-size: 12px; color: #dc2626"
                >
                  Please select an optimization direction.
                </div>
              </v-col>

              <!-- Skill Assessment -->
              <v-col cols="12">
                <div class="section-label mb-1">Required Skills Assessment</div>
                <div
                  style="font-size: 12px; color: #9ca3af; margin-bottom: 10px"
                >
                  Select all skill assessments relevant to this criterion
                </div>
                <div
                  v-if="skill_assessment_list.length == 0"
                  class="no-criteria-box rounded-xl d-flex align-center justify-center py-4"
                  style="color: #9ca3af; font-size: 13px"
                >
                  No skill assessments available
                </div>
                <div class="skill-grid" v-else>
                  <div
                    v-for="(i, idx) in skill_assessment_list"
                    :key="i.id"
                    class="skill-item rounded-lg pa-3"
                    :class="i.isSelected ? 'skill-item--selected' : ''"
                    @click="setSelected(idx)"
                  >
                    <div class="d-flex align-center ga-2">
                      <v-icon
                        size="15"
                        :color="i.isSelected ? '#059669' : '#d1d5db'"
                        >{{
                          i.isSelected
                            ? "mdi-check-circle"
                            : "mdi-circle-outline"
                        }}</v-icon
                      >
                      <div>
                        <div
                          style="
                            font-size: 12.5px;
                            font-weight: 600;
                            color: #111827;
                          "
                        >
                          {{ i.title }}
                        </div>
                        <div style="font-size: 11px; color: #9ca3af">
                          {{ i.category }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />
          <v-card-actions class="px-6 py-3">
            <v-spacer />
            <v-btn
              variant="text"
              color="#6b7280"
              rounded="lg"
              @click="add_criteria_dialog = false"
              >Cancel</v-btn
            >
            <v-btn
              color="#00A63E"
              variant="flat"
              class="text-white"
              rounded="lg"
              :disabled="btnLoading"
              :loading="btnLoading"
              @click="
                criteriaAction == 'Add' ? saveCriteria() : updateCriteria()
              "
            >
              <v-icon start>mdi-check-circle-outline</v-icon>
              {{
                criteriaAction == "Add" ? "Add Criterion" : "Update Criterion"
              }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <fade-away-message-component
      displayType="default"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    ></fade-away-message-component>
  </div>
</template>

<script>
import eventBus from "@/eventBus";
export default {
  props: {
    data: Object,
    action: String,
    programs: Array,
    batch: Number,
  },
  data() {
    return {
      dialog: false,
      title: null,
      criteriaAction: null,
      criteria_name: null,
      skill_assessment_list: [],
      criteriaList: [],
      add_criteria_dialog: false,
      description: null,
      weight: null,
      btnLoading: false,
      criteria_optimization: null,
      optimizationError: false,
      dss_criterion_ids: null,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
      criteriaIndex: null,
    };
  },
  watch: {
    data: {
      handler(data) {
        this.dialog = true;
        this.initialize();
        this.$refs.DSSCriterionForm.resetValidation();
        if (data.id) {
          this.title = data.title;
          this.description = data.description;
          this.dss_criterion_ids = data.dss_criterion_ids;
          this.criteriaList = data.criteriaList;
        } else {
          this.$refs.DSSCriterionForm.reset();
          this.criteriaList = [];
        }
      },
      deep: true,
    },
  },
  computed: {
    getTotalCriteriaWeights() {
      let total = 0;
      if (this.criteriaList.length > 0) {
        for (let i = 0; i < this.criteriaList.length; i++) {
          total += parseFloat(this.criteriaList[i].weight);
        }
      }
      return total;
    },
  },
  methods: {
    initialize() {
      this.getSkillAssessments();
      this.userId = this.$store.state.user.id;
    },
    setSelected(index) {
      this.skill_assessment_list[index].isSelected =
        !this.skill_assessment_list[index].isSelected;
    },

    add_criteria() {
      this.criteriaAction = "Add";
      this.add_criteria_dialog = true;
      this.optimizationError = false;
      this.skill_assessment_list.map((data) => {
        data.isSelected = false;
      });
      this.$refs.CriterionForm.reset();
      this.criteria_optimization = null;
    },
    edit_criteria(item, index) {
      this.criteriaAction = "Update";
      this.optimizationError = false;
      for (let i = 0; i < this.skill_assessment_list.length; i++) {
        this.skill_assessment_list[i].isSelected = false;
      }
      this.criteriaIndex = index;
      for (let j = 0; j < item.skill_required.length; j++) {
        for (let i = 0; i < this.skill_assessment_list.length; i++) {
          if (item.skill_required[j].id == this.skill_assessment_list[i].id) {
            this.skill_assessment_list[i].isSelected = true;
          }
        }
      }
      this.criteria_name = item.name;
      this.weight = item.weight;
      this.criteria_optimization = item.criteria_optimization;
      this.add_criteria_dialog = true;
    },

    delete_criteria(idx) {
      let affirm = confirm("Are you sure you want to delete this criteria?");
      if (affirm) this.criteriaList.splice(idx, 1);
    },
    async saveCriteria() {
      let formValid = await this.$refs.CriterionForm.validate();
      if (!this.criteria_optimization) {
        this.optimizationError = true;
        return;
      }
      this.optimizationError = false;
      if (formValid.valid) {
        let selected = this.skill_assessment_list.filter(
          (a) => a.isSelected == true,
        );
        if (selected.length == 0) {
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.message = "Please Select Skills Assessment.";
          this.fadeAwayMessage.header = "System Message";
        } else {
          this.criteriaList.push({
            name: this.criteria_name,
            weight: this.weight,
            criteria_optimization: this.criteria_optimization,
            skill_required: selected,
          });
          this.add_criteria_dialog = false;
        }
      }
    },
    async updateCriteria() {
      let formValid = await this.$refs.CriterionForm.validate();
      if (!this.criteria_optimization) {
        this.optimizationError = true;
        return;
      }
      this.optimizationError = false;
      if (formValid.valid) {
        let selected = this.skill_assessment_list.filter(
          (a) => a.isSelected == true,
        );
        this.criteriaList[this.criteriaIndex].name = this.criteria_name;
        this.criteriaList[this.criteriaIndex].weight = this.weight;
        this.criteriaList[this.criteriaIndex].criteria_optimization =
          this.criteria_optimization;
        this.criteriaList[this.criteriaIndex].skill_required = selected;
        this.add_criteria_dialog = false;
      }
    },

    getSkillAssessments() {
      this.axiosCall(
        "/skill-assessement/program/" +
          this.programs[0].program_id +
          "/" +
          this.batch,
        "GET",
      ).then((res) => {
        if (res) {
          this.skill_assessment_list = res.data;
          for (let i = 0; i < this.skill_assessment_list.length; i++) {
            Object.assign(this.skill_assessment_list[i], { isSelected: false });
          }
          this.loading = false;
        }
      });
    },
    async save() {
      let formValid = await this.$refs.DSSCriterionForm.validate();
      if (formValid.valid) {
        this.btnLoading = true;
        if (this.criteriaList.length == 0) {
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.message = "Please add criterias.";
          this.fadeAwayMessage.header = "System Message";
          this.btnLoading = false;
        } else {
          let data = {
            title: this.title,
            description: this.description,
            program_ids: JSON.stringify(this.programs),
            criterias: JSON.stringify(this.criteriaList),
            student_batch_id: this.batch,
          };
          this.axiosCall("/dss-criteria", "POST", data).then((res) => {
            if (res.data.status == 201) {
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "success";
              this.fadeAwayMessage.message = res.data.msg;
              this.fadeAwayMessage.header = "System Message";
              this.btnLoading = false;
              this.closeD();
            } else {
              this.btnLoading = false;
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "error";
              this.fadeAwayMessage.message = res.data.msg;
              this.fadeAwayMessage.header = "System Message";
            }
          });
        }
      }
    },

    async update() {
      let formValid = await this.$refs.DSSCriterionForm.validate();
      if (formValid.valid) {
        this.btnLoading = true;
        if (this.criteriaList.length == 0) {
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.message = "Please add criterias.";
          this.fadeAwayMessage.header = "System Message";
          this.btnLoading = false;
        } else {
          let data = {
            dss_criterion_ids: JSON.stringify(this.dss_criterion_ids),
            title: this.title,
            description: this.description,
            program_ids: JSON.stringify(this.programs),
            criterias: JSON.stringify(this.criteriaList),
          };
          this.axiosCall("/dss-criteria/update_criteria", "POST", data).then(
            (res) => {
              if (res.data.status == 200) {
                this.fadeAwayMessage.show = true;
                this.fadeAwayMessage.type = "success";
                this.fadeAwayMessage.message = res.data.msg;
                this.fadeAwayMessage.header = "System Message";
                this.btnLoading = false;
                this.closeD();
              } else {
                this.btnLoading = false;
                this.fadeAwayMessage.show = true;
                this.fadeAwayMessage.type = "error";
                this.fadeAwayMessage.message = res.data.msg;
                this.fadeAwayMessage.header = "System Message";
              }
            },
          );
        }
      }
    },

    closeD() {
      eventBus.emit("closeDSSCriterionDialog", true);
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
.dlg-header {
  background: #fff;
  border-bottom: none;
}
.dlg-icon-box {
  width: 36px;
  height: 36px;
  background: #00a63e;
  flex-shrink: 0;
}
.section-label {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.no-criteria-box {
  border: 1.5px dashed #e5e7eb;
  background: #f9fafb;
}
.criteria-card {
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  transition: box-shadow 0.15s;
}
.criteria-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
}
.criteria-num {
  width: 28px;
  height: 28px;
  background: #dcfce7;
  color: #059669;
  font-size: 12px;
  font-weight: 800;
}
.opt-card {
  border: 1.5px solid #e5e7eb;
  background: #f9fafb;
  transition: all 0.15s;
}
.opt-card:hover {
  border-color: #d1d5db;
  background: #fff;
}
.opt-card--green {
  border-color: #a7f3d0 !important;
  background: #f0fdf4 !important;
}
.opt-card--orange {
  border-color: #fde68a !important;
  background: #fffbeb !important;
}
.skill-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
.skill-item {
  border: 1.5px solid #e5e7eb;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.15s;
}
.skill-item:hover {
  border-color: #d1d5db;
  background: #fff;
}
.skill-item--selected {
  border-color: #a7f3d0 !important;
  background: #f0fdf4 !important;
}
</style>
